import { useReactiveVar } from '@apollo/client';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  IconButton,
  Grid,
  Container,
  Typography,
  Switch,
  Link as MuiLink,
  Card,
  useTheme
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import {
  PriceInterval,
  Product,
  ProductType
} from 'common/types/graphql-types';
import { useGetProductsLazyQuery } from 'payment/queries/getProductsQuery';
import { checkoutRoute } from 'payment/routes';
import { Checkout } from './Checkout';
import { selectedProduct } from 'payment/schema/schema';
import { useEffect, useState } from 'react';
import { ChangeEvent } from 'react';
import { useCallback } from 'react';
import { FC } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import getConfig from 'next/config';
import { useMeBillingQuery } from 'user/queries/meBilling';
import { Link } from 'common/components/Link';
import { useAnalytics } from 'common/hooks/useAnalytics';
import { PRODUCT_SELECTED, TOGGLE_UPGRADE_MODAL } from 'payment/events';

const { publicRuntimeConfig } = getConfig();

export const TieredPricingDisplay: FC<{
  revisionId?: string;
  redirect?: boolean;
}> = ({ children, revisionId }) => {
  const analytics = useAnalytics();
  const theme = useTheme();
  const { data: meData } = useMeBillingQuery({ fetchPolicy: 'no-cache' });
  const [interval, setInterval] = useState<PriceInterval>(PriceInterval.Month);
  const [getSubscriptions, { data: subscriptionData }] =
    useGetProductsLazyQuery({
      fetchPolicy: 'cache-and-network'
    });

  const [getProducts, { data: productData }] = useGetProductsLazyQuery();

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setInterval(checked ? PriceInterval.Year : PriceInterval.Month);
    },
    []
  );

  const createHandleSelect = useCallback(
    (product: Partial<Product & { revisionId?: string }>) => () => {
      const price = product.prices?.[0];
      analytics.capture(PRODUCT_SELECTED, {
        product: product.id,
        price: price?.id,
        amount: price?.amount,
        interval: price?.interval
      });
      selectedProduct(product);
    },
    []
  );

  useEffect(() => {
    getSubscriptions({
      variables: {
        interval,
        type: ProductType.Recurring
      }
    });
  }, [interval]);

  useEffect(() => {
    if (!revisionId) {
      return;
    }
    getProducts({
      variables: {
        type: ProductType.OneTime
      }
    });
  }, [revisionId]);

  if (!subscriptionData) {
    return null;
  }
  return (
    <Container maxWidth={'md'}>
      <Grid
        container
        alignItems={'flex-end'}
        justifyContent={'center'}
        spacing={3}
      >
        <Grid item xs={12}>
          {!!children ? (
            children
          ) : (
            <>
              <Box textAlign={'center'}>
                <Typography variant={'h4'}>Choose a plan</Typography>
              </Box>
              <Box textAlign={'center'} marginTop={1} marginBottom={3}>
                <Typography variant={'body1'}>
                  Choose the features that will best help your workflow
                </Typography>
              </Box>
            </>
          )}
          <Box textAlign={'center'} marginTop={1}>
            <Grid
              component='label'
              container
              justifyContent={'center'}
              alignItems='center'
              spacing={1}
            >
              <Grid item>Monthly</Grid>
              <Grid item>
                <Switch
                  color={'primary'}
                  checked={interval === PriceInterval.Year}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item>Yearly</Grid>
            </Grid>
          </Box>
        </Grid>
        {subscriptionData?.products?.results?.map((product, _, arr) => {
          const isCurrentSubscription =
            meData?.me?.subscription?.product?.id === product.id &&
            product.prices[0].id ===
              meData?.me?.subscription?.product?.prices?.[0].id;
          return (
            <Grid
              key={product.prices[0].id}
              item
              xs={12}
              md={(12 / arr.length) as 4}
            >
              <Box position={'relative'}>
                {product.calloutText && (
                  <Typography
                    variant={'overline'}
                    sx={{
                      display: 'block',
                      position: 'relative',
                      background: theme.palette.secondary.dark,
                      padding: theme.spacing(2),
                      bottom: theme.spacing(-1),
                      textAlign: 'center',
                      letterSpacing: 3,
                      borderRadius: '12px 12px 0 0',
                      width: '100%'
                    }}
                  >
                    {product.calloutText}
                  </Typography>
                )}
                <Card
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    height: theme.spacing(45),
                    padding: theme.spacing(3),
                    justifyContent: 'space-between',
                    borderRadius: theme.spacing(1),
                    ...(product.calloutText
                      ? {
                          border: `1px solid ${theme.palette.secondary.dark}`
                        }
                      : {})
                  }}
                >
                  <Typography>{product.name}</Typography>
                  <Box marginTop={1} marginBottom={2}>
                    <Typography variant={'h4'}>
                      ${product.prices[0].amount}{' '}
                      {product.prices[0].interval &&
                        `/${product.prices[0].interval.toLowerCase()}`}
                    </Typography>
                  </Box>
                  {product.features.map((feature, i) => (
                    <Box
                      marginTop={1}
                      key={`${feature}-${i}`}
                      display={'flex'}
                      flexDirection={'row'}
                    >
                      <Box marginRight={2}>
                        <Check color={'primary'} fontSize={'small'} />
                      </Box>
                      <Typography variant={'caption'} color={'textSecondary'}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                  <Box marginTop={2} margin={2} marginBottom={0}>
                    <Link
                      underline={'none'}
                      color={'inherit'}
                      href={checkoutRoute()}
                    >
                      <Button
                        color={'primary'}
                        fullWidth
                        disabled={isCurrentSubscription}
                        variant={'contained'}
                        onClick={createHandleSelect(product)}
                      >
                        {isCurrentSubscription ? 'Your Current Plan' : 'Select'}
                      </Button>
                    </Link>
                  </Box>
                </Card>
              </Box>
            </Grid>
          );
        })}

        {productData?.products?.results?.map((product) => {
          return (
            <Grid key={product.id} item xs={4}>
              <Button
                color={'primary'}
                fullWidth
                variant={'contained'}
                onClick={createHandleSelect({ ...product, revisionId })}
              >
                {`$${product.prices?.[0]?.amount} ${product.name}`}
              </Button>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
};

export const TieredPricingDisplayDialog: FC<
  DialogProps & { revisionId?: string }
> = ({ revisionId, ...props }) => {
  const theme = useTheme();
  const analytics = useAnalytics();
  const product = useReactiveVar(selectedProduct);

  useEffect(() => {
    if (props.open) {
      analytics.capture(TOGGLE_UPGRADE_MODAL, {
        isOpen: true
      });
    }
    if (publicRuntimeConfig.stripeApiKey && props.open) {
      loadStripe(publicRuntimeConfig.stripeApiKey);
    }
  }, [props.open]);

  const handleClose = useCallback(() => {
    analytics.capture(TOGGLE_UPGRADE_MODAL, {
      isOpen: false
    });

    if (!props.onClose) {
      return;
    }
    props.onClose({}, 'escapeKeyDown');
  }, [props.onClose]);

  return (
    <Dialog fullWidth maxWidth={product ? 'lg' : 'md'} {...props}>
      <DialogContent
        sx={{
          padding: theme.spacing(5),
          background:
            'linear-gradient(169.91deg, #22222B 0.46%, #281A33 111.41%) !important'
        }}
      >
        {product ? (
          <>
            <IconButton
              sx={{
                position: 'absolute',
                top: theme.spacing(1),
                right: theme.spacing(1)
              }}
              onClick={handleClose}
              size='large'
            >
              <Close />
            </IconButton>
            <Checkout onComplete={handleClose} />
          </>
        ) : (
          <>
            <TieredPricingDisplay revisionId={revisionId} redirect={false}>
              <Typography variant={'h4'} align={'center'}>
                Upgrade to get access to this feature
              </Typography>
              <Box textAlign={'center'} marginTop={1} marginBottom={3}>
                <Typography variant={'body1'}>
                  In order to access this feature, please select a plan that
                  includes it
                </Typography>
              </Box>
              <IconButton
                sx={{
                  position: 'absolute',
                  top: theme.spacing(1),
                  right: theme.spacing(1)
                }}
                onClick={handleClose}
                size='large'
              >
                <Close />
              </IconButton>
            </TieredPricingDisplay>
            <Box textAlign={'center'} marginTop={2}>
              <MuiLink
                color={'textSecondary'}
                underline={'always'}
                onClick={handleClose}
              >
                No thanks, I don’t actually have work to do
              </MuiLink>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
